import { useCallback, useRef } from "react";
import type { SseParsedEvent } from "../api/sse/sseParser";
import type {
  AssistantMessageDelta,
  StreamingAssistantMessage,
} from "../Components/ChatPanel/ChatThread/ChatMessage/messageTypes";
import { buildAssistantMessage } from "../Utils/dataNormalization";

export type ChatStreamEvent =
  | { type: "started" }
  | { type: "message_delta"; data: AssistantMessageDelta }
  | { type: "done" }
  | { type: "error"; data: string };

interface StreamState {
  isStreaming: boolean;
  streamingChatId: string | null;
}

interface UseStreamEventHandlersParams {
  updateStreamState: (newState: Partial<StreamState>) => void;
  cancelStreamingToast: () => void;
  scheduleWorkingToastIfOnChatPage: (chatId: string) => void;
  showDoneToast: (chatId: string) => void;
  onChatRenamed: (chatId: string, newName: string) => void;
  onStreamComplete: (params: {
    chat_uuid: string;
    userMessage: { content: string; message_uuid: string } | null;
    assistantMessage: StreamingAssistantMessage | null;
  }) => void;
}

interface UseStreamEventHandlersReturn {
  handleMessageEvent: (
    event: SseParsedEvent,
    abortController: AbortController,
    chat_uuid: string
  ) => void;
  registerEventHandler: (handler: (event: ChatStreamEvent) => void) => void;
  unregisterEventHandler: () => void;
  getAccumulatedMessage: () => StreamingAssistantMessage | null;
  getPendingUserMessage: () => { content: string; message_uuid: string } | null;
  // Called by startStream before kicking off the API request
  resetForNewStream: (
    pendingUserMessage: { content: string; message_uuid: string } | null,
    initialAssistantMessage: StreamingAssistantMessage
  ) => void;
  // Called by stopStream after aborting the API request
  clearStreamRefs: () => void;
  handleStreamComplete: (
    chat_uuid: string,
    shouldOnlyAppendUserMessage?: boolean,
    clearRefsAfter?: boolean
  ) => void;
}

const useStreamEventHandlers = ({
  updateStreamState,
  cancelStreamingToast,
  scheduleWorkingToastIfOnChatPage,
  showDoneToast,
  onChatRenamed,
  onStreamComplete,
}: UseStreamEventHandlersParams): UseStreamEventHandlersReturn => {
  // Only one ChatProvider can be active at a time (single-chat routing), so a
  // single event handler ref is sufficient. If that assumption ever changes,
  // this should become a Set of handlers.
  const eventHandlerRef = useRef<((event: ChatStreamEvent) => void) | null>(
    null
  );
  const pendingUserMessageRef = useRef<{
    content: string;
    message_uuid: string;
  } | null>(null);
  const accumulatedMessageRef = useRef<StreamingAssistantMessage | null>(null);
  const hasCompletedRef = useRef(false);

  const handleStreamComplete = useCallback(
    (
      chat_uuid: string,
      shouldOnlyAppendUserMessage = false,
      clearRefsAfter = false
    ) => {
      if (hasCompletedRef.current) {
        return;
      }
      hasCompletedRef.current = true;
      onStreamComplete({
        chat_uuid,
        userMessage: pendingUserMessageRef.current,
        assistantMessage: shouldOnlyAppendUserMessage
          ? null
          : accumulatedMessageRef.current,
      });
      if (clearRefsAfter) {
        accumulatedMessageRef.current = null;
        pendingUserMessageRef.current = null;
      }
    },
    [onStreamComplete]
  );

  const handleStreamStarted = useCallback(
    (chat_uuid: string) => {
      updateStreamState({ isStreaming: true });
      eventHandlerRef.current?.({ type: "started" });
      scheduleWorkingToastIfOnChatPage(chat_uuid);
    },
    [updateStreamState, scheduleWorkingToastIfOnChatPage]
  );

  const handleStreamDone = useCallback(
    (chat_uuid: string, messageUuid?: string) => {
      // The `done` event carries the message's persisted id (a global uuid).
      // Stamp it onto the accumulated message so the completed message — and its
      // feedback buttons — reference the real id instead of the empty placeholder.
      if (messageUuid && accumulatedMessageRef.current) {
        accumulatedMessageRef.current = {
          ...accumulatedMessageRef.current,
          message_uuid: messageUuid,
        };
      }
      eventHandlerRef.current?.({ type: "done" });
      handleStreamComplete(chat_uuid, false, true);
      updateStreamState({ isStreaming: false, streamingChatId: null });
      cancelStreamingToast();
      showDoneToast(chat_uuid);
    },
    [
      handleStreamComplete,
      updateStreamState,
      cancelStreamingToast,
      showDoneToast,
    ]
  );

  const handleMessageEvent = useCallback(
    (
      event: SseParsedEvent,
      abortController: AbortController,
      chat_uuid: string
    ) => {
      if (abortController.signal.aborted && event.event !== "error") {
        return;
      }
      switch (event.event) {
        case "started":
          handleStreamStarted(chat_uuid);
          break;
        case "message_delta": {
          const delta = event.data as unknown as AssistantMessageDelta;
          accumulatedMessageRef.current = buildAssistantMessage(
            delta,
            accumulatedMessageRef.current
          );
          eventHandlerRef.current?.({ type: "message_delta", data: delta });
          break;
        }
        case "chat_renamed":
          onChatRenamed(
            chat_uuid,
            (event.data as { new_name: string }).new_name
          );
          break;
        case "done": {
          const doneData = event.data as { message_id?: string } | undefined;
          handleStreamDone(chat_uuid, doneData?.message_id);
          break;
        }
        case "error":
          eventHandlerRef.current?.({
            type: "error",
            data: event.data as string,
          });
          handleStreamComplete(chat_uuid, true, true);
          updateStreamState({ isStreaming: false, streamingChatId: null });
          cancelStreamingToast();
          break;
        default:
          break;
      }
    },
    [
      handleStreamStarted,
      handleStreamDone,
      handleStreamComplete,
      onChatRenamed,
      updateStreamState,
      cancelStreamingToast,
    ]
  );

  const registerEventHandler = useCallback(
    (handler: (event: ChatStreamEvent) => void) => {
      eventHandlerRef.current = handler;
    },
    []
  );

  const unregisterEventHandler = useCallback(() => {
    eventHandlerRef.current = null;
  }, []);

  const getAccumulatedMessage = useCallback(
    () => accumulatedMessageRef.current,
    []
  );

  const getPendingUserMessage = useCallback(
    () => pendingUserMessageRef.current,
    []
  );

  const resetForNewStream = useCallback(
    (
      pendingUserMessage: { content: string; message_uuid: string } | null,
      initialAssistantMessage: StreamingAssistantMessage
    ) => {
      hasCompletedRef.current = false;
      pendingUserMessageRef.current = pendingUserMessage;
      accumulatedMessageRef.current = initialAssistantMessage;
    },
    []
  );

  const clearStreamRefs = useCallback(() => {
    accumulatedMessageRef.current = null;
    pendingUserMessageRef.current = null;
  }, []);

  return {
    handleMessageEvent,
    registerEventHandler,
    unregisterEventHandler,
    getAccumulatedMessage,
    getPendingUserMessage,
    resetForNewStream,
    clearStreamRefs,
    handleStreamComplete,
  };
};

export default useStreamEventHandlers;
